// Checkout cross-sell — two complementary maps + honest incentive
// framing. Rendered inside step-1/step-2 of the checkout column so the
// visitor sees them exactly when they're most receptive: after price
// discovery, before payment.
//
// Copy stays consistent with the boutique's bulk incentive block: same
// "-15 % dès 3 cartes / -20 % dès 5" language, same free-shipping
// threshold. We render three signals side by side:
//   • the two recommended maps (with badge + starting price)
//   • the bulk-tier savings still on the table (next tier + € saved)
//   • the missing amount for free shipping (100 €), or "Livraison
//     offerte" when the current total already crosses.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";

// ─── Keep these two constants in sync with ShopProductPage.jsx ─────
const FREE_SHIPPING_THRESHOLD_CENTS = 10000; // 100 €
const BULK_TIERS = [
  { min: 5, pct: 20, label: "-20 % dès 5 cartes" },
  { min: 3, pct: 15, label: "-15 % dès 3 cartes" },
  { min: 2, pct: 10, label: "-10 % dès 2 cartes" },
];
const currentBulkTier = (q) => BULK_TIERS.find((t) => q >= t.min) || null;
const nextBulkTier = (q) => [...BULK_TIERS].reverse().find((t) => q < t.min) || null;
const formatCents = (c) => `${(c / 100).toFixed(2).replace(".", ",")} €`;

const CheckoutCrossSell = ({ productId, currentQuantity = 1, currentUnitCents = 0, onIncrement }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { ok, data } = await apiFetch(`/api/shop/products/${productId}/related?limit=2`);
      if (cancelled) return;
      setItems(ok && data?.items ? data.items : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [productId]);

  // Compute the current line total (with the active bulk discount) so
  // the two nudges below use the same number the customer sees on the
  // "Total" row. Falls back to 0 when unit price hasn't loaded yet.
  const totalCents = useMemo(() => {
    if (!currentUnitCents) return 0;
    const base = currentUnitCents * currentQuantity;
    const t = currentBulkTier(currentQuantity);
    return t ? Math.round(base * (1 - t.pct / 100)) : base;
  }, [currentUnitCents, currentQuantity]);

  const nextTier = nextBulkTier(currentQuantity);
  const missingUnits = nextTier ? nextTier.min - currentQuantity : 0;
  // Savings the visitor would unlock by hitting the next tier. Compare
  // the (units_at_next_tier × unit × (1 - pct)) vs. (units_at_next_tier × unit)
  // so the amount matches what they'll actually see in their cart.
  const savingsAtNextTier = useMemo(() => {
    if (!nextTier || !currentUnitCents) return 0;
    const gross = currentUnitCents * nextTier.min;
    const netAtTier = Math.round(gross * (1 - nextTier.pct / 100));
    const netToday = (() => {
      const t = currentBulkTier(nextTier.min - 1);
      const g = currentUnitCents * (nextTier.min - 1);
      return t ? Math.round(g * (1 - t.pct / 100)) : g;
    })();
    // We want the customer to compare adding N more cards vs. staying
    // where they are: return the € they save on the marginal cards.
    return Math.max(0, netToday + currentUnitCents * missingUnits - netAtTier);
  }, [nextTier, currentUnitCents, missingUnits]);

  const missingForFreeShippingCents = Math.max(0, FREE_SHIPPING_THRESHOLD_CENTS - totalCents);
  const freeShippingUnlocked = totalCents >= FREE_SHIPPING_THRESHOLD_CENTS && totalCents > 0;
  const freeShippingProgress = totalCents
    ? Math.min(100, Math.round((totalCents / FREE_SHIPPING_THRESHOLD_CENTS) * 100))
    : 0;

  if (loading) {
    return (
      <div className="border-t border-[color:var(--gv-line)] pt-4 mt-4 flex justify-center" data-testid="checkout-crosssell-loading">
        <Loader2 size={16} className="animate-spin text-[color:var(--gv-ink)]/30" />
      </div>
    );
  }
  if (!items.length && !currentUnitCents) return null;

  return (
    <div className="border-t border-[color:var(--gv-line)] pt-5 mt-5 space-y-4" data-testid="checkout-crosssell">
      {/* Free-shipping strip — always shown so the customer sees at a
          glance where they stand vs. the 100 € threshold. */}
      {currentUnitCents > 0 && (
        <div data-testid="crosssell-free-shipping">
          {freeShippingUnlocked ? (
            <div className="flex items-center gap-2 text-[13px] text-green-700 bg-green-50 border border-green-200 rounded-[5px] px-3 py-2">
              <Truck size={14} />
              <span><strong>Livraison offerte</strong> — vous avez atteint le palier {formatCents(FREE_SHIPPING_THRESHOLD_CENTS)}.</span>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between text-[12.5px] text-[color:var(--gv-ink)]/80 mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <Truck size={13} className="text-[color:var(--gv-pink)]" />
                  Il manque <strong>{formatCents(missingForFreeShippingCents)}</strong> pour la livraison gratuite
                </span>
                <span className="tabular-nums text-[color:var(--gv-ink)]/50">{freeShippingProgress}%</span>
              </div>
              <div className="h-1.5 bg-[color:var(--gv-mist)] rounded-full overflow-hidden">
                <div className="h-full bg-[color:var(--gv-pink)] transition-all" style={{ width: `${freeShippingProgress}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk-tier nudge — only shown when there's a next tier the
          customer hasn't unlocked yet, so we don't clutter the review
          step of an already-optimal 5+ cart order. */}
      {nextTier && currentUnitCents > 0 && (
        <button
          type="button"
          onClick={onIncrement}
          className="w-full text-left bg-[color:var(--gv-pink)]/8 border border-[color:var(--gv-pink)]/30 rounded-[5px] px-3 py-2.5 flex items-center gap-3 hover:bg-[color:var(--gv-pink)]/12 transition-colors"
          data-testid="crosssell-bulk-nudge"
        >
          <Sparkles size={16} className="text-[color:var(--gv-pink)] shrink-0" />
          <div className="flex-1 text-[13px] text-[color:var(--gv-ink)]">
            <div>
              <strong>+{missingUnits} carte{missingUnits > 1 ? "s" : ""}</strong> = <strong>{nextTier.label}</strong>
              {savingsAtNextTier > 0 && (
                <> — économisez <strong className="text-[color:var(--gv-pink)]">{formatCents(savingsAtNextTier)}</strong></>
              )}
            </div>
          </div>
          <span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] shrink-0">+1 →</span>
        </button>
      )}

      {/* Two complementary maps — same region first, then same
          product type, best-sellers fallback (backend rule). */}
      {items.length > 0 && (
        <div data-testid="crosssell-related">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">
            Complétez votre collection · -10 % à -20 % dès 2 cartes
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {items.map((it) => (
              <CrossSellCard key={it.product_id} item={it} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Kept separate so the parent stays readable — this card has a bit of
// its own layout logic (price extraction + optional badge).
const CrossSellCard = ({ item }) => {
  // Match the listing shape (`_public_product` without detail=True):
  // it exposes `from_amount_cents` + `preview`. Falls back to `variants`
  // shape used by the product-detail endpoint.
  const startingPriceCents = useMemo(() => {
    if (item.from_amount_cents) return item.from_amount_cents;
    const prices = (item.variants || [])
      .map((v) => v.amount_cents || v.retail_amount_cents)
      .filter(Boolean);
    return prices.length ? Math.min(...prices) : 0;
  }, [item]);

  const previewUrl = item.preview || item.preview_url;
  const badge = item.meta?.badge;

  return (
    <Link
      to={`/shop/${item.product_id}`}
      className="group flex gap-3 items-center bg-white border border-[color:var(--gv-line)] rounded-[5px] p-2.5 hover:border-[color:var(--gv-pink)] transition-colors"
      data-testid={`crosssell-card-${item.product_id}`}
    >
      <div className="w-16 h-16 shrink-0 rounded-[4px] overflow-hidden bg-[color:var(--gv-mist)]">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>
      <div className="flex-1 min-w-0">
        {badge && (
          <div className="text-[9.5px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-0.5">
            {badge}
          </div>
        )}
        <div className="font-display text-[13px] text-[color:var(--gv-ink)] leading-tight line-clamp-2 group-hover:text-[color:var(--gv-pink)]">
          {item.title?.split(" | ")[0] || item.title}
        </div>
        <div className="text-[12px] text-[color:var(--gv-ink)]/60 mt-1 flex items-center justify-between">
          <span>Dès <strong className="text-[color:var(--gv-ink)]">{formatCents(startingPriceCents)}</strong></span>
          <ArrowRight size={12} className="text-[color:var(--gv-ink)]/40 group-hover:text-[color:var(--gv-pink)] group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </Link>
  );
};

export default CheckoutCrossSell;
